import { useState, useEffect, useCallback } from 'react';
import { captureError } from '@/shared/utils/error-reporting';

export enum PersistentStorageKeys {
  GeneratingProcs = 'generating_procs',
  OrgChats = 'org_chats',
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function usePersistentStorage<T extends Record<string, any>>(
  key: PersistentStorageKeys,
  initialValue: T,
): [T, (value: T | ((val: T) => T)) => void] {
  // Get from local storage then parse stored json or return initialValue
  const readValue = useCallback((): T => {
    try {
      const item = window.localStorage.getItem(key);
      return item ? (JSON.parse(item) as T) : initialValue;
    } catch (error) {
      captureError(error, {
        module: 'use_persistent_storage',
        action: 'read_value',
      });
      return initialValue;
    }
  }, [key, initialValue]);

  // State to store our value
  const [storedValue, setStoredValue] = useState<T>(readValue);

  // Return a wrapped version of useState's setter function that ...
  // ... persists the new value to localStorage.
  const setValue = useCallback(
    (value: T | ((val: T) => T)) => {
      try {
        // Allow value to be a function so we have same API as useState
        const valueToStore =
          value instanceof Function ? value(storedValue) : value;

        // Save to state
        setStoredValue(valueToStore);

        // Save to local storage
        if (Object.keys(valueToStore).length === 0) {
          window.localStorage.removeItem(key);
        } else {
          window.localStorage.setItem(key, JSON.stringify(valueToStore));
        }

        // Dispatch a custom event for cross-tab synchronization
        window.dispatchEvent(
          new StorageEvent('storage', {
            key: key,
            newValue: JSON.stringify(valueToStore),
          }),
        );
      } catch (error) {
        captureError(error, {
          module: 'use_persistent_storage',
          action: 'set_value',
        });
      }
    },
    [key, storedValue],
  );

  // Listen for changes in other tabs/windows
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === key) {
        try {
          if (e.newValue === null) {
            setStoredValue(initialValue);
          } else {
            setStoredValue(JSON.parse(e.newValue));
          }
        } catch (error) {
          captureError(error, {
            module: 'use_persistent_storage',
            action: 'handle_storage_event',
          });
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [key, initialValue]);

  // Also sync with localStorage on mount and when key changes
  useEffect(() => {
    const item = window.localStorage.getItem(key);
    if (item) {
      try {
        setStoredValue(JSON.parse(item));
      } catch (error) {
        captureError(error, {
          module: 'use_persistent_storage',
          action: 'sync_with_local_storage',
        });
      }
    }
  }, [key]);

  return [storedValue, setValue];
}
